import React from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import { connect } from 'react-redux';
import { browserHistory, Link } from 'react-router';
import moment from 'moment';
import Clear from 'material-ui/svg-icons/content/clear';
import Done from 'material-ui/svg-icons/action/done';
import IconButton from "material-ui/IconButton";
import Edit from "material-ui/svg-icons/image/edit";
import Devices from "material-ui/svg-icons/hardware/devices-other";
import { red300, lightGreen500, grey500, grey200 } from 'material-ui/styles/colors';
import 'react-table/react-table.css';
import Toggle from 'material-ui/Toggle';
import { translate } from 'react-i18next';

import { dateFromISO } from '../utils/helpers';
import CommonTable from '../components/CommonTable';
import TextFilterField from '../components/filters/TextFilterField';
import YesNoFilterField from '../components/filters/YesNoFilterField';
import DateRangeFilterField from '../components/filters/DateRangeFilterField';
import * as endUserActions from '../redux/modules/endUsers';
import * as packetboxActions from '../redux/modules/packetboxes';
import i18n from '../translations/i18n';
import UserDevices from '../components/UserDevices';
import ConfirmDialog from '../components/ConfirmDialog';

/*
PropTypes / DefaultProps
 */
const defaultProps = {
  loading: false,
  endUsers: {},
};

const propTypes = {
  loading: PropTypes.bool,
  endUsers: PropTypes.object,
  fetchEndUsers: PropTypes.func,
  t: PropTypes.func,
  packetboxes: PropTypes.any,
  fetchPacketboxes: PropTypes.func,
};

class EndUserList extends React.Component {
  constructor(props) {
    super(props);

    this.devices = null;
    this.state = {
      showOnlyValid: true,
      devices: null,
      confirmDialogOpened: false,
    };
  }
  /*
    Lifecycle methods
   */
  componentWillMount() {
    this.fetchData();
  }

  /*
    Custom methods
   */

  openUserDevices = (id) => {
    this.setState({
      confirmDialogOpened: true,
      endUserId: id,
    });
  }

  cancelActivationDialog = () => {
    this.setState({
      confirmDialogOpened: false,
      endUserId: null,
    });
  }

  getDeviceColumns = () => [
    {
      header: 'test1',
    },
    {
      header: 'test2',
    },
    {
      header: 'test3',
    }
  ]

  getSubcomponentData = (userId) => {
    const data = [];

    if (this.props.packetboxes) {
      Object.keys(this.props.packetboxes).forEach((key) => {
        const pkb = this.props.packetboxes[key];

        if (pkb.owner_id === userId) {
          data.push(pkb);
        }
      });
    }

    return data;
  };

  getSubcomponentColumns = () => [
    {
      header: this.props.t('id'),
      accessor: 'id',
      className: 'centerAlign',
      width: 60,
    },
    {
      header: this.props.t('paketbox'),
      accessor: 'name',
      className: 'centerAlign',
      render: obj => <Link to={`/packetboxes/modify/${obj.row.id}`}>{obj.row.name}</Link>,
    },
    {
      header: this.props.t('state'),
      accessor: 'state',
      className: 'centerAlign',
      render: row => this.props.t(row.value),
    },
    {
      header: this.props.t('last_measurement'),
      accessor: 'last_measurement',
      className: 'centerAlign',
      render: row => dateFromISO(row.value),
    },
  ];

  initColumns() {
    const styles = {
      editButton: {
        fill: grey500,
        backgroundColor: grey200,
        borderRadius: "20px"
      },
      editButtonIcon: {
        height: 24,
        width: 24
      }
    };

    this.columns = [
      {
        header: this.props.t('id'),
        accessor: 'id',
        className: 'centerAlign',
        width: 60,
        filterRender: ({ filter, onFilterChange }) => (
          <TextFilterField
            type="number"
            name="id"
            filter={filter}
            onFilterChange={onFilterChange}
          />
        ),
        apiRequest: {
          filterTransform: value => parseInt(value, 10),
          operator: 'eq',
        },
      },
      {
        header: this.props.t('username'),
        accessor: 'username',
        filterMethod: (filter, row) =>
          row.username.toLowerCase().startsWith(filter.value.toLowerCase()),
        filterRender: ({ filter, onFilterChange }) => (
          <TextFilterField name="username" filter={filter} onFilterChange={onFilterChange} />
        ),
      },
      {
        header: this.props.t('last_name'),
        accessor: 'last_name',
        filterMethod: (filter, row) =>
          row.last_name.toLowerCase().startsWith(filter.value.toLowerCase()),
        filterRender: ({ filter, onFilterChange }) => (
          <TextFilterField name="last_name" filter={filter} onFilterChange={onFilterChange} />
        ),
      },
      {
        header: this.props.t('first_name'),
        accessor: 'first_name',
        filterMethod: (filter, row) =>
          row.first_name.toLowerCase().startsWith(filter.value.toLowerCase()),
        filterRender: ({ filter, onFilterChange }) => (
          <TextFilterField name="first_name" filter={filter} onFilterChange={onFilterChange} />
        ),
      },

      {
        header: this.props.t('code'),
        show: false,
        accessor: 'code',
        filterMethod: (filter, row) =>
          row.code.toLowerCase().startsWith(filter.value.toLowerCase()),
        filterRender: ({ filter, onFilterChange }) => (
          <TextFilterField name="code" filter={filter} onFilterChange={onFilterChange} />
        ),
      },

      {
        header: this.props.t('email'),
        accessor: 'email',
        filterMethod: (filter, row) =>
          row.email.toLowerCase().startsWith(filter.value.toLowerCase()),
        filterRender: ({ filter, onFilterChange }) => (
          <TextFilterField name="email" filter={filter} onFilterChange={onFilterChange} />
        ),
      },

      {
        header: this.props.t('time_registered'),
        accessor: 'time_created',
        render: row => dateFromISO(row.value),
        filterRender: ({ filter, onFilterChange }) => (
          <DateRangeFilterField
            name="time_created"
            filter={filter}
            onFilterChange={onFilterChange}
          />
        ),
        filterMethod: (filter, row) => {
          if (!filter.value) return true;
          const startDate = filter.value.startDate.startOf('day');
          const endDate = filter.value.endDate.endOf('day');
          const rowDate = moment(row.time_created);

          return rowDate.isAfter(startDate) && rowDate.isBefore(endDate);
        },
      },
      {
        header: this.props.t('active'),
        accessor: 'is_active',
        className: 'centerAlign',
        render: row => (row.value ? <Done color={lightGreen500} /> : <Clear color={red300} />),
        filterRender: ({ filter, onFilterChange }) => (
          <YesNoFilterField name="is_active" filter={filter} onFilterChange={onFilterChange} />
        ),
        filterMethod: (filter, row) => row.is_active === filter.value,
      },
      {
        header: this.props.t('deleted'),
        show: !this.state.showOnlyValid,
        width: 100,
        accessor: 'valid',
        className: 'centerAlign',
        render: row => (!row.value ? <Done color={lightGreen500} /> : <Clear color={red300} />),
        filterRender: ({ filter, onFilterChange }) => (
          <YesNoFilterField name="valid" filter={filter} onFilterChange={onFilterChange} />
        ),
        filterMethod: (filter, row) => row.valid !== filter.value,
      },
      {
        header: this.props.t('actions'),
        accessor: 'actions',
        className: 'centerAlign',
        hideFilter: true,
        sortable: false,
        style: { overflow: "visible" },
        render: rowProps => {
          return (
            <span>
              <IconButton
                className="iconButton"
                onTouchTap={() => this.itemEdit(rowProps.row.id)}
                iconStyle={styles.editButton}
              >
                <Edit style={styles.editButtonIcon} />
              </IconButton>
              <IconButton
                className="iconButton"
                onTouchTap={() => {
                  this.openUserDevices(rowProps.row.id);
                }}
                iconStyle={styles.editButton}
              >
                <Devices style={styles.editButtonIcon} />
              </IconButton>
            </span>
          );
        }
      },
    ];
  }

  fetchData = () => {
    this.props.fetchPacketboxes();
    this.props.fetchEndUsers();
  };

  itemEdit = (id) => {
    browserHistory.push(`/end-users/modify/${id}`);
  };

  itemCreate = () => {
    browserHistory.push('/end-users/new');
  };

  toggleDeleted = (event, isInputChecked) => {
    this.setState({
      showOnlyValid: !isInputChecked,
    });
  };

  render() {
    this.initColumns();

    let data = [];
    if (this.props.endUsers) {
      data = Object.values(this.props.endUsers);

      if (this.state.showOnlyValid) {
        data = data.filter(user => user.valid);
      }
    }

    const deletedToggle = (
      <Toggle
        label={this.props.t('show_deleted')}
        onToggle={this.toggleDeleted}
        style={{ marginTop: 20 }}
      />
    );

    return (
      <div>
        <CommonTable
          title={this.props.t('user_list')}
          data={data}
          onItemCreate={this.itemCreate}
          loading={this.props.loading}
          columns={this.columns}
          onChange={this.fetchData}
          rightComponent={deletedToggle}
          SubComponent={cellInfo => (
            <div style={{ padding: '20px' }}>
              <ReactTable
                title={this.props.t('assigned_paketboxes')}
                data={this.getSubcomponentData(cellInfo.row.id)}
                columns={this.getSubcomponentColumns()}
                defaultPageSize={3}
                showPagination={false}
                noDataText={this.props.t('user_no_paketbox_assigned')}
                pageSize={this.getSubcomponentData(cellInfo.row.id).length || 3}
              />
            </div>
          )}
        />
        {!this.state.endUserId ?
          '' : 
            (<ConfirmDialog
              className="endUserDevices"
              closeWithoutSave
              title={this.props.t("endUserDevices")}
              text={<UserDevices endUserId={this.state.endUserId} />}
              open={this.state.confirmDialogOpened}
              onConfirm={this.cancelActivationDialog}
              onCancel={this.cancelActivationDialog}
            />)
        }
      </div>
    );
  }
}

EndUserList.defaultProps = defaultProps;
EndUserList.propTypes = propTypes;

function mapStateToProps(state) {
  return {
    loading: state.endUsers.fetching,
    endUsers: state.endUsers.entities,
    packetboxes: state.packetboxes.entities,
    devices: state.endUsers.devices,
  };
}

const actions = Object.assign({}, endUserActions, packetboxActions);

const extended = translate(['translations'], { i18n })(EndUserList);
export default connect(mapStateToProps, actions)(extended);



// WEBPACK FOOTER //
// ./src/containers/EndUserList.js